<script lang="ts">
	import { HighlightedText, Icon } from '@repo/commons/components'
	import { generateId } from '@repo/commons/utils'
	import type { TooltipProps } from '$lib_commons/components/Tooltip.svelte'
	import { DEFAULT_TOOLTIP_PROPS } from '@repo/commons/constants'

	interface Props {
		label?: string
		size?: 'xs' | 'sm' | 'md'
		helperText?: string
		tooltip?: TooltipProps
		value?: string
		placeholder?: string
		readonly?: boolean
		disabled?: boolean
		clearable?: boolean
		required?: boolean
		name?: string
		id?: string
		showError?: boolean
		errorMessage?: string
		autofocus?: boolean
		className?: string
		rows?: number
		cols?: number
		resize?: 'none' | 'vertical' | 'horizontal' | 'both'
		maxlength?: number
		minlength?: number
		highlightPattern?: string
		onInput?: (value: string) => void
		onFocus?: () => void
		onBlur?: () => void
		onClick?: (e: Event) => void
		onHighlightMatch?: (match: string, isExactMatch: boolean) => void
		onKeyDown?: (e: KeyboardEvent) => void
		allowLineBreaks?: boolean
	}

	let {
		label = '',
		size = 'sm',
		helperText = '',
		tooltip,
		value = '',
		placeholder = '',
		errorMessage = '',
		required = false,
		showError = false,
		readonly = false,
		disabled = false,
		clearable = false,
		autofocus = false,
		name,
		id,
		className = '',
		rows = 3,
		cols,
		resize = 'vertical',
		highlightPattern,
		maxlength,
		minlength,
		onHighlightMatch = () => {},
		onInput = () => {},
		onFocus = () => {},
		onBlur = () => {},
		onClick = () => {},
		onKeyDown = () => {},
		allowLineBreaks = true
	}: Props = $props()

	const defaultId = id ?? generateId()

	const tooltipProps = $derived({ ...DEFAULT_TOOLTIP_PROPS, ...tooltip })

	function handleInput(event: Event) {
		value = (event.target as HTMLTextAreaElement).value
		onInput(value)
	}
</script>

<div>
	<label for={defaultId} class="label-wrapper">
		{#if label}
			<span class="label">
				{#if highlightPattern}
					<HighlightedText text={label} pattern={highlightPattern} {onHighlightMatch} />
				{:else}
					{label}
				{/if}
				{#if required}
					<span class="required-indicator">*</span>
				{/if}
				{#if tooltip?.text || tooltip?.html}
					{#await import('@repo/commons/components') then { Tooltip }}
						<Tooltip {...tooltipProps}>
							<Icon name="info" size="sm" class="text-brand" />
						</Tooltip>
					{/await}
				{/if}
			</span>
		{/if}

		{#if helperText}
			<p class="helper-text">
				{helperText}
			</p>
		{/if}

		<div
			class={['textarea-wrapper', className]}
			class:invalid={showError}
			class:xs={size === 'xs'}
			class:sm={size === 'sm'}
			class:md={size === 'md'}
			class:disabled
		>
			<textarea
				id={defaultId}
				class="textarea-field flex-1 outline-0 rounded-md focus:outline-none"
				class:invalid={showError}
				class:is-clear-icon-shown={clearable && value && !disabled}
				class:resize-none={resize === 'none'}
				class:resize-y={resize === 'vertical'}
				class:resize-x={resize === 'horizontal'}
				class:resize={resize === 'both'}
				bind:value
				{placeholder}
				{autofocus}
				{readonly}
				{disabled}
				{name}
				{rows}
				{cols}
				{maxlength}
				{minlength}
				onclick={onClick}
				onkeydown={(event) => {
					if (!allowLineBreaks && event.key === 'Enter') {
						event.preventDefault()

						return
					}

					onKeyDown(event)
				}}
				oninput={handleInput}
				onfocus={onFocus}
				onblur={onBlur}
			></textarea>
		</div>
	</label>

	{#if showError}
		<div class="error-message">
			<div class="field-error-icon-wrapper">
				<Icon name="fieldError" class="text-red-400" />
			</div>

			<p class="error-text">{errorMessage}</p>
		</div>
	{/if}
</div>

<style>
	.prepend-wrapper {
		@apply flex items-start mt-2 ml-4;
	}
	.append-wrapper {
		@apply flex items-start mt-2 mr-4;
	}
	.textarea-wrapper {
		@apply mt-1 bg-white border-[1px] border-[#002C4D80] border-solid relative min-h-[120px] flex w-full rounded-lg overflow-hidden;
		transition: outline-width 0.1s ease-in-out;
	}
	.textarea-wrapper.disabled {
		@apply bg-gray-100;
	}
	.textarea-wrapper:focus-within {
		@apply outline outline-2 outline-brand outline-offset-2;
	}
	.textarea-wrapper.invalid:focus-within {
		@apply outline-[#E4485B]/30;
	}
	.textarea-wrapper.md {
		@apply min-h-[140px];
	}
	.textarea-wrapper.sm {
		@apply min-h-[100px];
	}

	.textarea-wrapper.xs {
		@apply min-h-[80px];
	}

	.textarea-wrapper.invalid {
		@apply border-[#E4485B] bg-[#E4485B]/5;
	}
	.textarea-field {
		width: inherit;
		padding: 12px 16px;
		@apply max-w-full bg-transparent outline-0 min-h-full;
	}
	.textarea-field.is-clear-icon-shown {
		@apply pr-2;
	}
	.textarea-field::placeholder {
		@apply text-[#001D33B2];
	}
	.textarea-field.resize-none {
		resize: none;
	}
	.textarea-field.resize-y {
		resize: resize-vertical;
	}
	.textarea-field.resize-x {
		resize: resize-horizontal;
	}
	.textarea-field.resize {
		resize: initial;
	}
	.clear-button {
		@apply w-6 mr-4 h-6 text-lg absolute top-2 right-0;
	}
	.clear-button.has-appended-element {
		@apply mr-1;
	}
	.label-wrapper {
		@apply block;
	}
	.label {
		@apply text-base text-[#001626E5] inline-flex items-center gap-1;
	}
	.required-indicator {
		@apply text-[#001D33B2];
	}
	.helper-text {
		@apply mb-0.5 text-sm text-[#001D33B2];
	}
	.error-message {
		@apply flex items-center min-h-8 my-1 text-red-400;
	}
	.error-text {
		@apply text-[#E4485B] text-sm font-semibold ml-2;
	}
	.field-error-icon-wrapper {
		@apply flex;
	}
</style>
